/**
 * Visitor analytics — what the shop actually needs to see.
 *
 * GA4 and the Meta pixel are already on the site, but neither answers the
 * question the admin panel is being asked: who is on the site right now, what
 * are they looking at, and where did the ones who did not order drop off. GA4
 * forbids storing anything that identifies a visitor, aggregates on a delay,
 * and knows nothing about a fabric id or a cart. So events land in Firestore
 * alongside the orders they lead to, and the admin panel reads them live.
 *
 * Two writes per event: the raw event, and an increment on the day's counter
 * document. The counters exist so the dashboard can draw a month of history
 * from thirty reads instead of thirty thousand.
 *
 * Nothing here may break a page. Every entry point swallows its own errors — a
 * visitor must never see analytics fail, and an order must never be lost to one.
 */

import firebase, { db, auth } from "../firebase";

const EVENTS = "Analytics_Events";
const VISITORS = "Analytics_Visitors";
const DAILY = "Analytics_Daily";

const VISITOR_KEY = "mv_vid";
const SESSION_KEY = "mv_sid";
const SESSION_SEEN_KEY = "mv_sseen";

// A session is a visit, not a tab. Half an hour of silence ends it, which is
// the window GA uses too, so the two roughly agree.
const SESSION_IDLE_MS = 30 * 60 * 1000;

/** Storage throws outright in some private modes, so every access is guarded. */
const readStore = (key) => {
    try {
        return window.localStorage.getItem(key);
    } catch (e) {
        return null;
    }
};

const writeStore = (key, value) => {
    try {
        window.localStorage.setItem(key, value);
        return true;
    } catch (e) {
        return false;
    }
};

const randomId = () => {
    try {
        if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    } catch (e) {
        // Older browsers and insecure origins: the fallback below is fine.
    }
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
};

/**
 * Crawlers and our own headless screenshots would otherwise show up as
 * visitors. Excluding them keeps the visitor count honest — a number the shop
 * is going to make decisions on.
 */
const isBot = () => {
    try {
        if (navigator.webdriver) return true;
        const ua = String(navigator.userAgent || "");
        if (!ua) return true;
        return /bot|crawl|spider|slurp|bingpreview|headless|lighthouse|pagespeed|gtmetrix|monitor|curl|wget|python-requests|axios|facebookexternalhit|embedly/i.test(ua);
    } catch (e) {
        return true;
    }
};

const deviceOf = () => {
    try {
        const ua = String(navigator.userAgent || "");
        if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) return "tablet";
        if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
        // A narrow desktop window is still a desktop, but a phone that lies
        // about its user agent is not — the width is the tie-breaker.
        if (window.innerWidth && window.innerWidth < 600) return "mobile";
        return "desktop";
    } catch (e) {
        return "unknown";
    }
};

/** Where the visit came from: a campaign tag if there is one, else the referrer. */
const sourceOf = () => {
    try {
        const utm = new URLSearchParams(window.location.search).get("utm_source");
        if (utm) return String(utm).slice(0, 60).toLowerCase();

        const ref = document.referrer;
        if (!ref) return "direct";
        const host = new URL(ref).hostname.replace(/^www\./, "");
        if (!host) return "direct";
        // Our own pages are not a traffic source; counting them would drown
        // out the real ones.
        if (host.endsWith("maviinci.in")) return "internal";
        return host.slice(0, 60).toLowerCase();
    } catch (e) {
        return "direct";
    }
};

const visitorState = () => {
    const stored = readStore(VISITOR_KEY);
    if (stored) return { id: stored, isNew: false };

    const id = randomId();
    // If storage refused the write we cannot tell this visitor from the next
    // one, so they go uncounted rather than counted afresh on every page.
    const persisted = writeStore(VISITOR_KEY, id);
    return { id, isNew: persisted };
};

const sessionState = () => {
    const stored = readStore(SESSION_KEY);
    const seenAt = Number(readStore(SESSION_SEEN_KEY)) || 0;
    const alive = Boolean(stored) && Date.now() - seenAt < SESSION_IDLE_MS;

    const id = alive ? stored : randomId();
    writeStore(SESSION_KEY, id);
    writeStore(SESSION_SEEN_KEY, String(Date.now()));

    return { id, isNew: !alive };
};

/**
 * The shop's day, not UTC's — "how many came today" has to mean today in
 * Bhilai. Shifting before slicing avoids pulling in a date library.
 */
export const dayKey = (date) => {
    const shifted = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
    return shifted.toISOString().slice(0, 10);
};

/** Only what is safe to keep — never a name, an address or a phone number. */
const identity = () => {
    try {
        const user = auth.currentUser;
        if (!user || user.isAnonymous) return { uid: "", signedIn: false };
        return { uid: user.uid, signedIn: true };
    } catch (e) {
        return { uid: "", signedIn: false };
    }
};

/**
 * A path with its ids taken out, for use as a counter key.
 *
 * The raw path is kept on the event itself. This is only for the day's
 * counters, where /fabric/aX9f and /fabric/bK2p must land on the same row or
 * the map grows without limit and the top-pages list becomes a list of one-hit
 * product ids instead of the pages people actually use.
 */
const looksLikeId = (seg) =>
    // A number, an encoded token, or an id with digits in it. A hyphen is the
    // tell that something is a written slug — /blog/how-to-take-body-measurements
    // is a page, not an id, and must not be collapsed away.
    /^\d+$/.test(seg)
    || (/[a-z]/.test(seg) && /[A-Z]/.test(seg))
    || (/\d/.test(seg) && /[a-z]/i.test(seg) && seg.indexOf("-") === -1)
    || (seg.length > 24 && seg.indexOf("-") === -1);

const pathKey = (path) => {
    const segments = String(path || "/")
        .split("/")
        .filter(Boolean)
        .slice(0, 3)
        .map((seg) => (looksLikeId(seg) ? ":id" : seg.toLowerCase()));
    // Firestore map keys cannot be empty and read badly with slashes.
    return segments.length ? segments.join("_").slice(0, 80) : "home";
};

/** Firestore rejects a document containing undefined, so nothing may be absent. */
const clean = (params) => {
    const out = {};
    if (!params || typeof params !== "object") return out;
    Object.keys(params).slice(0, 20).forEach((key) => {
        const value = params[key];
        if (value === undefined || value === null || value === "") return;
        if (typeof value === "number") out[key] = Number.isFinite(value) ? value : 0;
        else if (typeof value === "boolean") out[key] = value;
        else out[key] = String(value).slice(0, 200);
    });
    return out;
};

/**
 * Attach a contact to a visitor id.
 *
 * Deliberately not on the event: a phone number does not belong on every one
 * of a visitor's hundred page views. It is written once per visitor, to a
 * collection only the admin login can read, so the shop can put a name to the
 * person it is watching move through checkout and follow up when they drop.
 *
 * Only ever called with details the visitor typed into our own forms or signed
 * in with — nothing is inferred or bought in.
 */
export const identifyVisitor = (details = {}) => {
    try {
        if (typeof window === "undefined" || isBot()) return;

        const email = String(details.email || "").trim().toLowerCase();
        const phone = String(details.phone || "").replace(/[^\d+]/g, "");
        const name = String(details.name || "").trim();
        if (!email && !phone && !name) return;

        const visitor = visitorState();
        const who = identity();

        const patch = {
            visitorId: visitor.id,
            lastSeen: firebase.firestore.FieldValue.serverTimestamp(),
        };
        // A blank must never overwrite something we already know: forms fill in
        // different fields at different steps of the same visit.
        if (email && email.includes("@")) patch.email = email.slice(0, 120);
        if (phone.length >= 7) patch.phone = phone.slice(0, 20);
        if (name) patch.name = name.slice(0, 80);
        if (who.uid) patch.uid = who.uid;

        db.collection(VISITORS).doc(visitor.id).set(patch, { merge: true }).catch(() => {});
    } catch (e) {
        // Never worth an exception on a customer's screen.
    }
};

/**
 * Record one thing a visitor did.
 *
 * Fire and forget by design: callers sit on the order and checkout paths, and
 * a slow or failed analytics write must never delay or fail those.
 */
export const trackEvent = (name, params = {}) => {
    try {
        if (typeof window === "undefined" || !name) return;
        if (isBot()) return;

        const visitor = visitorState();
        const session = sessionState();
        const day = dayKey(new Date());
        const source = sourceOf();
        const device = deviceOf();
        const who = identity();
        const eventName = String(name).slice(0, 60);

        db.collection(EVENTS)
            .add({
                name: eventName,
                path: String(window.location.pathname || "/").slice(0, 300),
                title: String(document.title || "").slice(0, 160),
                visitorId: visitor.id,
                sessionId: session.id,
                newVisitor: visitor.isNew,
                newSession: session.isNew,
                uid: who.uid,
                signedIn: who.signedIn,
                source,
                device,
                day,
                params: clean(params),
                at: firebase.firestore.FieldValue.serverTimestamp(),
            })
            .catch(() => {});

        const inc = firebase.firestore.FieldValue.increment;
        const daily = {
            day,
            events: inc(1),
            byEvent: { [eventName]: inc(1) },
            byDevice: { [device]: inc(1) },
            bySource: { [source]: inc(1) },
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        };
        if (eventName === "page_view") {
            daily.pageViews = inc(1);
            daily.byPath = { [pathKey(window.location.pathname)]: inc(1) };
        }
        if (eventName === "order_placed") {
            daily.orders = inc(1);
            daily.revenue = inc(Math.round(Number(params && params.value) || 0));
        }
        if (visitor.isNew) daily.newVisitors = inc(1);
        if (session.isNew) daily.sessions = inc(1);

        db.collection(DAILY).doc(day).set(daily, { merge: true }).catch(() => {});
    } catch (e) {
        // Analytics is never worth an exception on a customer's screen.
    }
};

export default trackEvent;
